import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const LANGUAGES = [
  { code: 'tr', label: 'TR' },
  { code: 'en', label: 'EN' },
  { code: 'de', label: 'DE' },
  { code: 'fr', label: 'FR' },
  { code: 'ar', label: 'AR' },
  { code: 'ru', label: 'RU' },
];

interface LanguageSwitcherProps {
  dark?: boolean;
}

export default function LanguageSwitcher({ dark = false }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current =
    LANGUAGES.find((l) => l.code === i18n.language) ?? LANGUAGES[0];

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Language selector"
        aria-expanded={open}
        className={`flex items-center gap-2 whitespace-nowrap rounded-md border px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
          dark
            ? 'border-foreground-300/40 text-foreground-50 hover:border-primary-400 hover:text-primary-400'
            : 'border-foreground-300/40 text-foreground-950 hover:border-primary-500 hover:text-primary-600'
        }`}
      >
        <i className="ri-global-line text-base" aria-hidden="true"></i>
        <span>{current.label}</span>
        <i
          className={`ri-arrow-down-s-line text-sm transition-transform ${
            open ? 'rotate-180' : ''
          }`}
          aria-hidden="true"
        ></i>
      </button>

      {open && (
        <div className="absolute end-0 top-full z-50 mt-2 w-32 overflow-hidden rounded-lg border border-foreground-200/60 bg-background-50 py-1">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => changeLanguage(lang.code)}
              className={`flex w-full items-center justify-between px-4 py-2 text-sm transition-colors cursor-pointer hover:bg-primary-100/60 ${
                lang.code === current.code
                  ? 'font-semibold text-primary-600'
                  : 'text-foreground-700'
              }`}
            >
              <span className="whitespace-nowrap">{lang.label}</span>
              {lang.code === current.code && (
                <i className="ri-check-line text-primary-500" aria-hidden="true"></i>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}